import Link from "next/link";
import type { ParagraphLightCtaBanner as ParagraphLightCtaBannerData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./ParagraphLightCtaBanner.module.css";

/**
 * Layout 59 - `PARAGRAPH_LIGHT_CTA_BANNER_LAYOUT`: light CTA banner (mid-page call-out).
 *
 * Struktura:
 *   - Mint bg + zielony border top
 *   - 2-kol split: (opcjonalny eyebrow + tytul h3 + krotki opis) lewo, primary CTA (pill) + secondary CTA (outlined) + opcjonalny reassurance prawo
 *   - Secondary URL auto-wykrywa numer telefonu (prepend `tel:` gdy sam numer z + / cyframi / spacjami)
 */
export function ParagraphLightCtaBanner({
  paragraph,
}: {
  paragraph: ParagraphLightCtaBannerData;
  t: ParagraphTranslations;
}) {
  const { text_1: eyebrow, name: title, text: desc, primary, secondary, reassurance } = paragraph;
  const hasEyebrow = eyebrow && eyebrow.trim() !== "";

  return (
    <section className={styles.banner}>
      <div className={styles.left}>
        {hasEyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
        {title ? <h3 className={styles.title}>{title}</h3> : null}
        {desc ? <p className={styles.desc}>{desc}</p> : null}
      </div>
      <div className={styles.right}>
        <PrimaryCta label={primary.label} url={primary.url} />
        {secondary ? <SecondaryCta label={secondary.label} url={secondary.url} /> : null}
        {reassurance ? <p className={styles.reassurance}>{reassurance}</p> : null}
      </div>
    </section>
  );
}

function PrimaryCta({ label, url }: { label: string; url: string }) {
  const href = extractInternalHref(url);
  return href !== null ? (
    <Link href={href} className={styles.primaryBtn}>{label}</Link>
  ) : (
    <a href={url} className={styles.primaryBtn}>{label}</a>
  );
}

function SecondaryCta({ label, url }: { label: string; url: string }) {
  /* Auto-detect telefonu - admin wpisuje sam numer, frontend prepend `tel:`. */
  const isPhoneLike = /^[+\d][\d\s()\-]*$/.test(url.trim());
  const finalUrl = isPhoneLike ? `tel:${url.replace(/[\s()\-]/g, "")}` : url;
  const isPhone = isPhoneLike || finalUrl.startsWith("tel:");

  const inner = (
    <>
      {isPhone ? <span className={styles.phoneIcon} aria-hidden="true" /> : null}
      <span>{label}</span>
    </>
  );

  if (finalUrl.startsWith("tel:") || finalUrl.startsWith("mailto:")) {
    return <a href={finalUrl} className={styles.secondaryBtn}>{inner}</a>;
  }
  const href = extractInternalHref(finalUrl);
  return href !== null ? (
    <Link href={href} className={styles.secondaryBtn}>{inner}</Link>
  ) : (
    <a href={finalUrl} className={styles.secondaryBtn}>{inner}</a>
  );
}
